import { useEffect, useState } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';

import type {
    GetPracticeMemberInsightsOutput,
    OnboardingData,
    OnboardingStep,
    PracticeMember,
} from '@/entities/practice-member';

const STEPS: OnboardingStep[] = [
    'name',
    'why',
    'format',
    'length',
    'identifier',
    'treatment',
    'ehr',
    'building',
];

function getStepIndex(key: OnboardingStep): number {
    return STEPS.indexOf(key);
}

type Props = {
    serverStep: OnboardingStep | undefined;
    practiceMember: PracticeMember;
    insights: GetPracticeMemberInsightsOutput | undefined;
};

export function useOnboardingStep({ serverStep, practiceMember, insights }: Props) {
    const navigate = useNavigate();
    const { step: searchStep } = useSearch({ from: '/sign-up' });

    const serverStepIndex = serverStep ? getStepIndex(serverStep) : 0;
    const searchStepIndex = searchStep ? getStepIndex(searchStep) : -1;

    const [maxReachedIndex, setMaxReachedIndex] = useState(serverStepIndex);

    useEffect(() => {
        setMaxReachedIndex((prev) => Math.max(prev, serverStepIndex));
    }, [serverStepIndex]);

    const isRequestStepValid = searchStepIndex >= 0 && searchStepIndex <= maxReachedIndex;

    const currentStepIndex = isRequestStepValid ? searchStepIndex : maxReachedIndex;

    useEffect(() => {
        if (isRequestStepValid) {
            return;
        }

        navigate({
            to: '/sign-up',
            search: { step: STEPS[maxReachedIndex] },
            replace: true,
        });
    }, [isRequestStepValid, maxReachedIndex, navigate]);

    const stepForward = () => {
        const nextIndex = currentStepIndex + 1;
        if (nextIndex < STEPS.length) {
            setMaxReachedIndex(nextIndex);
            navigate({ to: '/sign-up', search: { step: STEPS[nextIndex] } });
        }
    };

    const stepBackward = () => {
        const prevIndex = currentStepIndex - 1;
        if (prevIndex >= 0) {
            navigate({ to: '/sign-up', search: { step: STEPS[prevIndex] } });
        }
    };

    const currentStep = STEPS[currentStepIndex];
    const nextStep = STEPS[currentStepIndex + 1] ?? null;
    const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

    const stepData: OnboardingData = {
        ...practiceMember,
        ...Object.fromEntries(
            Object.entries(insights || {}).map(([key, value]) => [key, { ...value, type: key }]),
        ),
    };

    return {
        currentStep,
        nextStep,
        progress,
        stepData,
        stepForward,
        stepBackward,
    };
}
